/**
 * E2E auth seam (docs/TESTING.md). When the build-time flag
 * `NEXT_PUBLIC_AUTH_TEST_MODE=1` is set, the app treats a localStorage marker as
 * the signed-in session instead of the BFF session cookie — so Playwright can
 * drive the real UI without a live Entra tenant. The flag is inlined by Next at
 * BUILD time: production builds never set it, so `AUTH_TEST_MODE` compiles to
 * `false` and every branch below is dead code — auth behavior is byte-for-byte
 * unchanged.
 *
 * This module deliberately touches NO token logic: in test mode
 * `acquireApiToken()` (src/lib/auth-client.ts) short-circuits to null and
 * requests go out without a bearer — fine, because E2E runs against a mocked
 * API.
 */

export const AUTH_TEST_MODE = process.env.NEXT_PUBLIC_AUTH_TEST_MODE === "1"

/** localStorage marker that stands in for the MSAL account in test mode. */
export const TEST_AUTH_STORAGE_KEY = "ap:test-auth"

/** The fake signed-in identity test mode injects. */
export const TEST_USER = {
  userId: "00000000-0000-4000-8000-00000000e2e0",
  name: "E2E Tester",
  email: "e2e@example.test",
} as const

type Listener = () => void
const listeners = new Set<Listener>()

function emit() {
  for (const fn of listeners) fn()
}

/** useSyncExternalStore subscribe — re-renders subscribers when the fake session flips. */
export function subscribeTestAuth(fn: Listener): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/** Snapshot for useSyncExternalStore. Always false outside test mode / on the server. */
export function isTestSignedIn(): boolean {
  if (!AUTH_TEST_MODE || typeof window === "undefined") return false
  try {
    return window.localStorage.getItem(TEST_AUTH_STORAGE_KEY) === "1"
  } catch {
    return false
  }
}

/** Test-mode stand-in for `loginRedirect` — flips the fake session on. */
export function signInTestUser(): void {
  if (!AUTH_TEST_MODE) return
  try {
    window.localStorage.setItem(TEST_AUTH_STORAGE_KEY, "1")
  } catch {
    return
  }
  emit()
}

/** Test-mode stand-in for `logoutRedirect` — flips the fake session off. */
export function signOutTestUser(): void {
  if (!AUTH_TEST_MODE) return
  try {
    window.localStorage.removeItem(TEST_AUTH_STORAGE_KEY)
  } catch {
    return
  }
  emit()
}
